import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm } from 'redux-form';

import * as PaymentsActions from '../../actions';
import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as PaymentsSelectors from '../../selectors';
import * as PaymentsUtils from '../../utils';

import '../UserSettings.scss';
import './SubscriptionModal.scss';

const log = D.Logger.child({ component: 'SubscriptionModal' });

const fields = ['currentPassword'];

const initialValues = {
  currentPassword: '',
};

export const SubscriptionModal = injectIntl(
  class extends React.Component {
    static propTypes = {
      path: PropTypes.string,
      updateFail: PropTypes.string,
      closeModal: PropTypes.func.isRequired,
      resetCreditCardForm: PropTypes.func.isRequired,
      deleteCreditCard: PropTypes.func.isRequired,
      clearSuccessMessage: PropTypes.func.isRequired,
      clearPasswordForm: PropTypes.func.isRequired,
      cancelSubscription: PropTypes.func.isRequired,
      exitFromFamilyPlan: PropTypes.func.isRequired,
      canOfferPremiumTrial: PropTypes.bool,
      activeTier: PropTypes.string,
      activeDiscountType: PropTypes.string,
      productTierIsPremium: PropTypes.bool,
      isDeletingCreditCard: PropTypes.bool,
      isUpdatingPaypal: PropTypes.bool,
      isCanceling: PropTypes.bool,
      isLeavingFamilyPlan: PropTypes.bool,
      isRemovingFamilyPlanChild: PropTypes.bool,
      hasUnsentFamilyPlanInvite: PropTypes.bool,
      childAccount: PropTypes.object,
      values: PropTypes.object,
      cardCompany: PropTypes.string,
      currency: PropTypes.string,
      isMobileDevice: PropTypes.bool,
      switchPlanDropDownIsOpen: PropTypes.bool,
      giftCardRemainingBalance: PropTypes.number,
      fields: PropTypes.shape({
        currentPassword: PropTypes.shape({
          value: PropTypes.string,
        }),
      }).isRequired,
      intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
        formatDate: PropTypes.func.isRequired,
      }).isRequired,
      removeChildFromFamilyPlan: PropTypes.func,
      escKeyPressed: PropTypes.bool,
      updateCreditCardInfo: PropTypes.func.isRequired,
      isChangingLowBalanceCreditCard: PropTypes.bool,
      handleParentCancel: PropTypes.func,
      stopSwitchingPlans: PropTypes.func,
    };

    static contextTypes = {
      router: PropTypes.object,
    };

    state = {
      password: '',
    };

    componentWillReceiveProps(newProps) {
      // TODO: WEBCLIENT-8592, WEBCLIENT-3560 – figure out a better way to fix the modal close issue than `!newProps.hasUnsentFamilyPlanInvite`
      if (newProps.updateSuccess && !newProps.hasUnsentFamilyPlanInvite) {
        this.props.closeModal();
        this.props.clearSuccessMessage();
        if (this.props.switchPlanDropDownIsOpen) {
          // If they're switching plans, close the modal and reset the CC success
          this.props.stopSwitchingPlans();
        }
      }

      if (newProps.escKeyPressed && this.props.escKeyPressed !== newProps.escKeyPressed) {
        this.closeModal();
      }
    }

    componentWillUnmount() {
      this.props.clearPasswordForm();
    }

    closeModal = () => {
      this.props.closeModal();
    };

    closeModalAndContinue = () => {
      this.context.router.push(D.Utils.Route.getMyMusicPath());
      this.closeModal();
    };

    closeModalAndUpdateCreditCard = () => {
      this.closeModal();
      this.props.updateCreditCardInfo();
    };

    handleInputChange = e => {
      this.setState({ password: e.target.value });
    };

    onSubmit = () => {
      if (this.props.isCanceling) {
        this.props.cancelSubscription(this.state.password);
      } else if (this.props.isDeletingCreditCard) {
        this.props.deleteCreditCard(this.state.password);
      } else if (this.props.isLeavingFamilyPlan) {
        this.props.exitFromFamilyPlan();
      } else if (this.props.isRemovingFamilyPlanChild) {
        this.props.removeChildFromFamilyPlan(
          this.props.childAccount.listenerId,
          this.props.childAccount.emailAddress,
        );
      }
    };

    renderHeaderText = () => {
      const {
        isLeavingFamilyPlan,
        isRemovingFamilyPlanChild,
        hasUnsentFamilyPlanInvite,
        isDeletingCreditCard,
        isChangingLowBalanceCreditCard,
        isCanceling,
      } = this.props;
      // If they're deleting their credit card or leaving family plan as a child account
      if (
        isDeletingCreditCard ||
        isLeavingFamilyPlan ||
        isRemovingFamilyPlanChild ||
        isChangingLowBalanceCreditCard
      ) {
        return <FormattedMessage {...PaymentsConstants.Messages.areYouSureConfirmation} />;
      }

      if (hasUnsentFamilyPlanInvite) {
        return (
          <FormattedMessage {...PaymentsConstants.Messages.unsentInvitesFamilyPlanConfirmation} />
        );
      }

      if (isCanceling) {
        return <FormattedMessage {...D.Constants.UserSettings.messages.cancelSubscription} />;
      }
    };

    renderInstructions = () => {
      const {
        childAccount,
        isCanceling,
        isLeavingFamilyPlan,
        isRemovingFamilyPlanChild,
        hasUnsentFamilyPlanInvite,
        activeTier,
        activeDiscountType,
        productTierIsPremium,
        isDeletingCreditCard,
        giftCardRemainingBalance,
        intl,
        currency,
      } = this.props; // TODO: WEBCLIENT-3560 – make this unified

      const { cancelSubscrText, cancelSubscrPremiumText, insufficientGiftCardBalanceForCancel } =
        D.Constants.UserSettings.messages;

      const instructionClasses = D.createBEM('Subscription__firstInstruction', {
        removeChild: this.props.isRemovingFamilyPlanChild,
      });

      const helpLinkClass = 'Subscription__firstInstruction__link';

      // If a user is deleting their credit card
      if (isDeletingCreditCard) {
        if (
          giftCardRemainingBalance > 0 &&
          giftCardRemainingBalance < PaymentsConstants.GIFT_CARD_WARNING_LIMIT
        ) {
          const pandoraHelpLink = (
            <a
              href={D.Constants.Urls.Urls.HELP}
              className={helpLinkClass}
              target='_blank'
              rel='noreferrer'
            >
              Pandora Help
            </a>
          );
          return (
            <div className={instructionClasses}>
              <div>
                <FormattedMessage
                  {...PaymentsConstants.Messages.deleteCreditCardInformation}
                  values={{
                    planName: PaymentsUtils.getPlanName(activeTier, activeDiscountType),
                  }}
                />
              </div>
              <div className='Subscription__firstInstruction__giftCardMessage'>
                <FormattedMessage
                  {...PaymentsConstants.Messages.insufficientGiftCardBalanceForCCDelete}
                  values={{
                    giftCardRemainingBalance: PaymentsUtils.formatPrice(
                      intl,
                      giftCardRemainingBalance,
                      currency,
                    ),
                    pandoraHelpLink,
                  }}
                />
              </div>
            </div>
          );
        }
        return (
          <div className={instructionClasses}>
            <FormattedMessage
              {...PaymentsConstants.Messages.deleteCreditCardInformation}
              values={{
                planName: PaymentsUtils.getPlanName(activeTier, activeDiscountType),
              }}
            />
          </div>
        );
      }

      // If a listener is leaving Family Plan as a child account
      if (isLeavingFamilyPlan) {
        return (
          <div className={instructionClasses}>
            <FormattedMessage
              {...PaymentsConstants.Messages.leaveFamilyPlanInformation}
              values={{
                planName: PaymentsUtils.getPlanName(activeTier, activeDiscountType),
              }}
            />
          </div>
        );
      }

      // If a listener is removing a child account from Family Plan
      if (isRemovingFamilyPlanChild) {
        return (
          <div className={instructionClasses}>
            <FormattedMessage
              {...PaymentsConstants.Messages.removeFamilyPlanChildInformation}
              values={{ emailAddress: childAccount.emailAddress }}
            />
          </div>
        );
      }

      // If a listener is trying to skip the invite/management page or has unsent invites
      if (hasUnsentFamilyPlanInvite) {
        return (
          <div className={instructionClasses}>
            <FormattedMessage
              {...PaymentsConstants.Messages.hasUnsentFamilyPlanInviteInformation}
            />
          </div>
        );
      }

      // If a user is canceling their subscription
      if (isCanceling) {
        const msg = productTierIsPremium ? cancelSubscrPremiumText : cancelSubscrText;
        if (
          giftCardRemainingBalance > 0 &&
          giftCardRemainingBalance < PaymentsConstants.GIFT_CARD_WARNING_LIMIT
        ) {
          const pandoraHelpLink = (
            <a
              href={D.Constants.Urls.Urls.HELP}
              className={helpLinkClass}
              target='_blank'
              rel='noreferrer'
            >
              Pandora Help
            </a>
          );
          return (
            <div className={instructionClasses}>
              <div>
                <FormattedMessage {...msg} />
              </div>
              <div className='Subscription__firstInstruction__giftCardMessage'>
                <FormattedMessage
                  {...insufficientGiftCardBalanceForCancel}
                  values={{
                    giftCardRemainingBalance: PaymentsUtils.formatPrice(
                      intl,
                      giftCardRemainingBalance,
                      currency,
                    ),
                    pandoraHelpLink,
                  }}
                />
              </div>
            </div>
          );
        }

        return (
          <div className={instructionClasses}>
            <FormattedMessage {...msg} />
          </div>
        );
      }

      if (this.props.isChangingLowBalanceCreditCard) {
        const pandoraHelpLink = (
          <a
            href={D.Constants.Urls.Urls.HELP}
            className={helpLinkClass}
            target='_blank'
            rel='noreferrer'
          >
            Pandora Help
          </a>
        );
        return (
          <div className={instructionClasses}>
            <FormattedMessage
              {...PaymentsConstants.Messages.insufficientGiftCardBalanceForCCUpdate}
              values={{
                giftCardRemainingBalance: PaymentsUtils.formatPrice(
                  intl,
                  giftCardRemainingBalance,
                  currency,
                ),
                pandoraHelpLink,
              }}
            />
          </div>
        );
      }

      log.error('Unable to determine instructions.');
      return (
        <div className={instructionClasses}>
          <FormattedMessage {...PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry} />
        </div>
      );
    };

    renderDismissLink = () => {
      return (
        <D.Button
          classes={['Button--white']}
          onClick={this.closeModal}
          qaTag={D.Constants.QATags.CB}
        >
          <FormattedMessage {...PaymentsConstants.Messages.dismiss} />
        </D.Button>
      );
    };

    renderCancelSubscriptionLink = () => {
      return (
        <D.Button
          className='Button'
          type='submit'
          isDisabled={!this.state.password}
          qaTag={D.Constants.QATags.SUBB}
        >
          <FormattedMessage {...D.Constants.UserSettings.messages.yesCancel} />
        </D.Button>
      );
    };

    renderLeavingFamilyPlanLink = () => {
      return (
        <D.Button type='submit' className='Button' qaTag={D.Constants.QATags.SUBB}>
          <FormattedMessage {...D.Constants.UserSettings.messages.familyConfirmLeavingFamilyPlan} />
        </D.Button>
      );
    };

    renderButtons = () => {
      const {
        closeModal,
        hasUnsentFamilyPlanInvite,
        isCanceling,
        isDeletingCreditCard,
        isLeavingFamilyPlan,
        isRemovingFamilyPlanChild,
        isMobileDevice,
      } = this.props;

      const buttonClasses = D.createBEM('SubscriptionModal__buttons', {
        removeChild: isRemovingFamilyPlanChild,
        deleteCard: isDeletingCreditCard,
      });

      if (isDeletingCreditCard) {
        return (
          <div className={buttonClasses}>
            <D.Button
              classes={['Button--white']}
              type='submit'
              isDisabled={!this.state.password}
              qaTag={D.Constants.QATags.SUBB}
            >
              <FormattedMessage {...PaymentsConstants.Messages.deleteCard} />
            </D.Button>
            <D.Button onClick={closeModal} className='Button' qaTag={D.Constants.QATags.CB}>
              <FormattedMessage {...PaymentsConstants.Messages.keepCard} />
            </D.Button>
          </div>
        );
      }

      if (this.props.isChangingLowBalanceCreditCard) {
        const handleCancel = event => {
          this.closeModal(event);
          this.props.handleParentCancel(event);
        };
        return (
          <div className={buttonClasses}>
            <D.Button
              classes={['Button--white']}
              onClick={handleCancel}
              qaTag={D.Constants.QATags.CB}
            >
              <FormattedMessage {...D.Constants.UserSettings.messages.cancel} />
            </D.Button>
            <D.Button onClick={this.closeModalAndUpdateCreditCard} qaTag={D.Constants.QATags.SUBB}>
              <FormattedMessage {...D.Constants.UserSettings.messages.continue} />
            </D.Button>
          </div>
        );
      }

      if (isCanceling) {
        if (isMobileDevice) {
          return (
            <div className={buttonClasses}>
              {this.renderCancelSubscriptionLink()}
              {this.renderDismissLink()}
            </div>
          );
        }

        return (
          <div className={buttonClasses}>
            {this.renderDismissLink()}
            {this.renderCancelSubscriptionLink()}
          </div>
        );
      }

      if (isLeavingFamilyPlan) {
        if (isMobileDevice) {
          return (
            <div className={buttonClasses}>
              {this.renderLeavingFamilyPlanLink()}
              {this.renderDismissLink()}
            </div>
          );
        }

        return (
          <div className={buttonClasses}>
            {this.renderDismissLink()}
            {this.renderLeavingFamilyPlanLink()}
          </div>
        );
      }

      if (isRemovingFamilyPlanChild) {
        return (
          <div className={buttonClasses}>
            <D.ButtonT3
              theme={D.ButtonT3Theme.LIGHT_TRANSPARENT}
              onClick={this.closeModal}
              qaTag={D.Constants.QATags.CB}
            >
              <FormattedMessage {...PaymentsConstants.Messages.dismiss} />
            </D.ButtonT3>
            <D.ButtonT3 type={D.ButtonT3Type.SUBMIT} qaTag={D.Constants.QATags.SUBB}>
              <FormattedMessage
                {...D.Constants.UserSettings.messages.familyConfirmRemovingFamilyPlanChild}
              />
            </D.ButtonT3>
          </div>
        );
      }

      if (hasUnsentFamilyPlanInvite) {
        return (
          <div className={buttonClasses}>
            <D.Button
              classes={['Button--white']}
              onClick={this.closeModal}
              qaTag={D.Constants.QATags.CB}
            >
              <FormattedMessage {...D.Constants.UserSettings.messages.goBack} />
            </D.Button>
            <D.Button onClick={this.closeModalAndContinue} qaTag={D.Constants.QATags.SUBB}>
              <FormattedMessage {...D.Constants.UserSettings.messages.continue} />
            </D.Button>
          </div>
        );
      }

      return (
        <div className={buttonClasses}>
          <D.Button
            classes={['Button--white']}
            onClick={this.closeModal}
            qaTag={D.Constants.QATags.CB}
          >
            <FormattedMessage {...PaymentsConstants.Messages.dismiss} />
          </D.Button>
          <D.Button type='submit' qaTag={D.Constants.QATags.SUBB}>
            <FormattedMessage {...D.Constants.UserSettings.messages.submit} />
          </D.Button>
        </div>
      );
    };

    // Only show password input if they're canceling or deleting credit card
    renderPasswordInput = () => {
      const {
        intl: { formatMessage: t },
      } = this.props;
      if (this.props.isCanceling || this.props.isDeletingCreditCard) {
        return (
          <input
            type='password'
            value={this.state.password}
            onChange={this.handleInputChange}
            placeholder={t(D.Constants.UserSettings.messages.password)}
            className='UserSettings__input'
            data-qa={D.Constants.QATags.CANCELP}
          />
        );
      }
    };

    render() {
      const { updateFail } = this.props;
      let message = '';
      let error = '';

      if (updateFail) {
        if (updateFail === 'AUTH_INVALID_USERNAME_PASSWORD') {
          message = (
            <FormattedMessage
              {...D.Constants.UserSettings.messages.passwordModalIncorrectPasswordError}
            />
          );
        } else if (this.props.isCanceling) {
          message = <FormattedMessage {...PaymentsConstants.Messages.somethingHasGoneWrong} />;
        }
        error = <div className='UserSettings__inputErrorMessage'>{message}</div>;
      }

      const inputWrapperClassnames = classNames({
        PasswordModalBody__row: true,
        'UserSettings__input--error': !!updateFail,
      });

      return (
        <D.DeprecatedForm className='SubscriptionModal' onSubmit={this.onSubmit}>
          <D.ModalTitle isSubscriptionModal={true}>{this.renderHeaderText()}</D.ModalTitle>
          <div className='SubscriptionModal__instructions'>{this.renderInstructions()}</div>

          <div className={inputWrapperClassnames}>
            {error}
            {this.renderPasswordInput()}
          </div>
          {this.renderButtons()}
        </D.DeprecatedForm>
      );
    }
  },
);

export function mapStateToProps(state) {
  const { updateFail, updateSuccess } = state.userAccount.toObject();

  const canOfferPremiumTrial = PaymentsSelectors.isPremiumTrialEligible(state);
  const activeTier = PaymentsSelectors.getActiveProductGroupTier(state);
  const activeDiscountType = PaymentsSelectors.getActiveProductDiscountType(state);
  const productTierIsPremium = PaymentsSelectors.productTierIsPremium(state);
  const path = D.Utils.Route.getCurrentPathnameFromState(state) || '';
  const currency = PaymentsSelectors.getActiveProductCurrency(state);
  const isResponsiveSubscriptionEnabled =
    D.Utils.TunerVars.getIsResponsiveSubscriptionEnabled(state);
  const isMobileDevice = isResponsiveSubscriptionEnabled && D.isMobileDevice(state);
  const isBlackhawkEnabled = D.Utils.TunerVars.getIsBlackhawkEnabled(state);
  const giftCardRemainingBalance =
    isBlackhawkEnabled && PaymentsSelectors.getGiftCardRemainingBalance(state);
  const switchPlanDropDownIsOpen = PaymentsSelectors.getIsSwitchingPlans(state);

  return {
    path,
    updateFail,
    activeTier,
    activeDiscountType,
    productTierIsPremium,
    canOfferPremiumTrial,
    initialValues,
    updateSuccess,
    currency,
    isMobileDevice,
    giftCardRemainingBalance,
    switchPlanDropDownIsOpen,
  };
}

export default reduxForm(
  {
    form: 'UserSettings_SubscriptionModal',
    fields,
    destroyOnUnmount: false,
  },
  mapStateToProps,
  {
    closeModal: D.Actions.Modal.closeModal,
    cancelSubscription: PaymentsActions.cancelSubscription,
    exitFromFamilyPlan: PaymentsActions.exitFromFamilyPlan,
    removeChildFromFamilyPlan: PaymentsActions.removeChildFromFamilyPlan,
    resetCreditCardForm: PaymentsActions.resetCreditCardForm,
    clearSuccessMessage: D.Actions.UserAccount.clearSuccessMessage,
    deleteCreditCard: PaymentsActions.deleteCreditCard,
    clearPasswordForm: D.Actions.UserAccount.clearPasswordForm,
    updateCreditCardInfo: PaymentsActions.updateCreditCardInfo,
    stopSwitchingPlans: PaymentsActions.stopSwitchingPlans,
  },
)(SubscriptionModal);
